import React, { useState, useEffect } from 'react';
import MaterialTable from "material-table";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';
import { SECONDORY_COLOR } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import AlertDialog from "../components/AlertDialog";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

const DeletionRequests = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const deletionrequestdata = useSelector(state => state.deletionrequestdata);
  const {
    fetchDeletionRequests,
    approveDeletionRequest,
    rejectDeletionRequest
  } = api;
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, row: null });

  useEffect(() => {
    dispatch(fetchDeletionRequests());
  }, [dispatch, fetchDeletionRequests]);

  useEffect(() => {
    setData(deletionrequestdata.requests || []);
  }, [deletionrequestdata.requests]);

  const columns = [
    { title: t('name'), field: 'name', editable: 'never' },
    { title: t('email'), field: 'email', editable: 'never' },
    { title: t('mobile'), field: 'mobile', editable: 'never' },
    {
      title: t('user_type'), field: 'usertype', editable: 'never',
      render: rowData => rowData.usertype ? t(rowData.usertype) : null
    },
    {
      title: t('requestDate'), field: 'requestDate', defaultSort: 'desc',
      render: rowData => rowData.requestDate ? moment(rowData.requestDate).format('lll') : null,
      exportTransformer: (rowData) => rowData.requestDate ? new Date(rowData.requestDate).toLocaleString() : ''
    },
    {
      title: t('status'), field: 'status', editable: 'never',
      render: rowData => t('deletion_status_' + rowData.status)
    },
    { title: t('reason'), field: 'reason', editable: 'never' },
    {
      title: t('processDate'), field: 'processDate',
      render: rowData => rowData.processDate ? moment(rowData.processDate).format('lll') : null
    }
  ];

  const handleApprove = (row) => {
    setConfirmDialog({ open: true, row });
  };

  const confirmApprove = async (confirmed) => {
    const row = confirmDialog.row;
    setConfirmDialog({ open: false, row: null });
    if (!confirmed || !row) return;
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t('demo_mode') });
      return;
    }
    const result = await dispatch(approveDeletionRequest(row, auth.profile ? auth.profile.uid : null));
    setCommonAlert({
      open: true,
      msg: result && result.ok ? t('deletion_request_approved') : (result?.message || t('update_failed'))
    });
  };

  const handleReject = async (row) => {
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t('demo_mode') });
      return;
    }
    const result = await dispatch(rejectDeletionRequest(row, auth.profile ? auth.profile.uid : null));
    setCommonAlert({
      open: true,
      msg: result && result.ok ? t('deletion_request_rejected') : (result?.message || t('update_failed'))
    });
  };

  return (
    deletionrequestdata.loading ? <CircularLoading /> :
      <ThemeProvider theme={theme}>
        <MaterialTable
          title={t('deletion_requests_title')}
          columns={columns}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
            padding: "20px",
          }}
          data={data}
          onRowClick={((evt, selected) => setSelectedRow(selected.tableData.id))}
          options={{
            pageSize: 10,
            pageSizeOptions: [10, 15, 20],
            exportButton: {
              csv: settings.AllowCriticalEditsAdmin,
              pdf: false,
            },
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE
            }),
            headerStyle: {
              position: "sticky",
              top: "0px",
              fontSize: "0.8em",
              fontWeight: 'bold ',
              color: colors.BLACK,
              backgroundColor: SECONDORY_COLOR,
              textAlign: "center",
              border: `1px solid ${colors.TABLE_BORDER}`,
              paddingRight: "20px",
              paddingLeft: "20px",
            },
            cellStyle: {
              border: `1px solid ${colors.TABLE_BORDER}`,
              textAlign: "center",
            },
            actionsColumnIndex: -1,
          }}
          localization={{
            toolbar: {
              searchPlaceholder: (t('search')),
              exportTitle: (t('export')),
            },
            header: {
              actions: (t('actions'))
            },
            pagination: {
              labelDisplayedRows: ('{from}-{to} ' + (t('of')) + ' {count}'),
              firstTooltip: (t('first_page_tooltip')),
              previousTooltip: (t('previous_page_tooltip')),
              nextTooltip: (t('next_page_tooltip')),
              lastTooltip: (t('last_page_tooltip'))
            },
          }}
          actions={[
            rowData => ({
              icon: 'check',
              tooltip: t('approve_deletion_request'),
              disabled: rowData.status !== 'pending',
              onClick: (event, row) => handleApprove(row)
            }),
            rowData => ({
              icon: 'close',
              tooltip: t('reject_deletion_request'),
              disabled: rowData.status !== 'pending',
              onClick: (event, row) => handleReject(row)
            }),
          ]}
        />
        <Dialog open={confirmDialog.open} onClose={() => confirmApprove(false)}>
          <DialogTitle>{t('approve_deletion_request')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('approve_deletion_confirm', {
                name: confirmDialog.row ? (confirmDialog.row.name || confirmDialog.row.email || confirmDialog.row.uid) : ''
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => confirmApprove(false)}>{t('cancel')}</Button>
            <Button onClick={() => confirmApprove(true)} style={{ color: colors.RED }}>
              {t('approve_deletion_request')}
            </Button>
          </DialogActions>
        </Dialog>
        <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
          {commonAlert.msg}
        </AlertDialog>
      </ThemeProvider>
  );
}

export default DeletionRequests;
